/**
 * Pipeline **room cm ↔ component usable frame px**: fit, margins, optional auto-orientation.
 */

export interface ComputeUppFloorPlanViewTransformParams {
  roomWidthCm: number;
  roomHeightCm: number;
  frameWidthPx: number;
  frameHeightPx: number;
  marginPx: number;
  /**
   * Extra px to subtract from the **usable width** when fitting at **0°** (room dimension line + label on the **right**).
   * Default **0**. Use `estimateRoomDimensionLabelOverflowPx` from this package for typical values.
   */
  dimensionInsetRightPx?: number;
  /**
   * Extra px to subtract from the **usable height** when fitting at **0°** (dimension line + label on the **bottom**).
   * Default **0**. Use `estimateRoomDimensionLabelOverflowPx` for typical values.
   */
  dimensionInsetBottomPx?: number;
  autoOptimizeOrientation: boolean;
  /**
   * Used when `autoOptimizeOrientation` is false. Must be **0** or **90**; other values are treated as **0**.
   */
  viewOrientationDeg: 0 | 90;
  /** Scale multiplier after fit (e.g. user zoom). */
  viewZoom: number;
}

export interface UppFloorPlanViewTransform {
  /** 0 or 90 (degrees, Konva-positive = counter-clockwise). */
  screenRotationDeg: number;
  /** Uniform scale cm → px in local content group coordinates. */
  scalePxPerCm: number;
  /** Content group origin in stage coordinates (px). */
  originXPx: number;
  originYPx: number;
  roomWidthCm: number;
  roomHeightCm: number;
}

function clampPositive(n: number, fallback: number): number {
  if (!Number.isFinite(n) || n <= 0) {
    return fallback;
  }
  return n;
}

function innerBoxForOrientation0(
  frameW: number,
  frameH: number,
  margin: number,
  insetR: number,
  insetB: number,
): { innerW: number; innerH: number } {
  const bw = Math.max(1, frameW - 2 * margin);
  const bh = Math.max(1, frameH - 2 * margin);
  return {
    innerW: Math.max(1, bw - insetR),
    innerH: Math.max(1, bh - insetB),
  };
}

/** When rotated 90°, reserve the larger gutter on both axes so dimension labels stay inside the host. */
function innerBoxForOrientation90(
  frameW: number,
  frameH: number,
  margin: number,
  insetR: number,
  insetB: number,
): { innerW: number; innerH: number } {
  const extra = Math.max(insetR, insetB);
  const bw = Math.max(1, frameW - 2 * margin);
  const bh = Math.max(1, frameH - 2 * margin);
  return {
    innerW: Math.max(1, bw - extra),
    innerH: Math.max(1, bh - extra),
  };
}

/**
 * Fits the room rectangle into the frame with margins and optional 0/90° orientation.
 */
export function computeUppFloorPlanViewTransform(
  p: ComputeUppFloorPlanViewTransformParams,
): UppFloorPlanViewTransform {
  const roomW = clampPositive(p.roomWidthCm, 1);
  const roomH = clampPositive(p.roomHeightCm, 1);
  const frameW = Math.max(1, p.frameWidthPx);
  const frameH = Math.max(1, p.frameHeightPx);
  const margin = Math.max(0, p.marginPx);
  const insetR = Math.max(0, p.dimensionInsetRightPx ?? 0);
  const insetB = Math.max(0, p.dimensionInsetBottomPx ?? 0);

  const inner0 = innerBoxForOrientation0(frameW, frameH, margin, insetR, insetB);
  const inner90 = innerBoxForOrientation90(frameW, frameH, margin, insetR, insetB);

  const scale0 = Math.min(inner0.innerW / roomW, inner0.innerH / roomH);
  const scale90 = Math.min(inner90.innerW / roomH, inner90.innerH / roomW);

  let screenRotationDeg: 0 | 90;
  let baseScale: number;
  if (p.autoOptimizeOrientation) {
    const use90 = scale90 > scale0 + 1e-9;
    screenRotationDeg = use90 ? 90 : 0;
    baseScale = use90 ? scale90 : scale0;
  } else {
    const want90 = p.viewOrientationDeg === 90;
    screenRotationDeg = want90 ? 90 : 0;
    baseScale = want90 ? scale90 : scale0;
  }
  const zoom = Number.isFinite(p.viewZoom) && p.viewZoom > 0 ? p.viewZoom : 1;
  const scalePxPerCm = baseScale * zoom;

  let originXPx: number;
  let originYPx: number;

  if (screenRotationDeg === 0) {
    const { innerW, innerH } = inner0;
    const fittedW = roomW * scalePxPerCm;
    const fittedH = roomH * scalePxPerCm;
    /**
     * `insetR` reserves room only on the **right** (dimension label), so centering within `innerW` alone
     * leaves the room visibly off-center toward the left by `insetR / 2` inside the full margin box `bw`.
     * Shift right by up to `insetR / 2` — capped at the actual fit slack `(innerW - fittedW) / 2` so the
     * label's reserved gutter never shrinks below `insetR` (no clipping) — to recenter within `bw` whenever
     * there is enough slack to do so.
     */
    const fitSlackHalf = Math.max(0, innerW - fittedW) / 2;
    const recenterShiftX = Math.min(insetR / 2, fitSlackHalf);
    originXPx = margin + (innerW - fittedW) / 2 + recenterShiftX;
    originYPx = margin + (innerH - fittedH) / 2;
  } else {
    const { innerW, innerH } = inner90;
    const fittedScreenW = roomH * scalePxPerCm;
    const fittedScreenH = roomW * scalePxPerCm;
    originXPx = margin + innerW / 2 + fittedScreenW / 2;
    originYPx = margin + innerH / 2 - fittedScreenH / 2;
  }

  return {
    screenRotationDeg,
    scalePxPerCm,
    originXPx,
    originYPx,
    roomWidthCm: roomW,
    roomHeightCm: roomH,
  };
}

/**
 * Room point in cm → Konva stage px for the current transform.
 */
export function cmPointToStagePx(
  t: UppFloorPlanViewTransform,
  xCm: number,
  yCm: number,
): { x: number; y: number } {
  const s = t.scalePxPerCm;
  if (t.screenRotationDeg === 0) {
    return { x: t.originXPx + xCm * s, y: t.originYPx + yCm * s };
  }
  return {
    x: t.originXPx - yCm * s,
    y: t.originYPx + xCm * s,
  };
}

/**
 * Stage px → room cm (inverse of {@link cmPointToStagePx}).
 */
export function stagePxToCmPoint(
  t: UppFloorPlanViewTransform,
  xPx: number,
  yPx: number,
): { xCm: number; yCm: number } {
  const s = t.scalePxPerCm;
  const lx = xPx - t.originXPx;
  const ly = yPx - t.originYPx;
  if (t.screenRotationDeg === 0) {
    return { xCm: lx / s, yCm: ly / s };
  }
  return { xCm: ly / s, yCm: -lx / s };
}

/**
 * English comment normalized.
 * English comment normalized.
 */
export function roomLocalPxToStagePx(
  t: UppFloorPlanViewTransform,
  lxPx: number,
  lyPx: number,
): { x: number; y: number } {
  return cmPointToStagePx(t, lxPx / t.scalePxPerCm, lyPx / t.scalePxPerCm);
}

/** Computes axis-aligned bounding box of the room rectangle in Stage coordinates. */
export function computeFloorPlanRoomRectStageAabb(
  t: UppFloorPlanViewTransform,
  roomWidthPx: number,
  roomHeightPx: number,
): { minX: number; minY: number; maxX: number; maxY: number } {
  const corners = [
    roomLocalPxToStagePx(t, 0, 0),
    roomLocalPxToStagePx(t, roomWidthPx, 0),
    roomLocalPxToStagePx(t, roomWidthPx, roomHeightPx),
    roomLocalPxToStagePx(t, 0, roomHeightPx),
  ];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of corners) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  return { minX, minY, maxX, maxY };
}
